import React, { useMemo, useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/site/primitives";
import { DONATION_PRESETS, SUPPORT_INTRO } from "@/content/support";
import { cn } from "@/lib/utils";


export const DonateWidget = () => {
  const [frequency, setFrequency] = useState("once"); // once | monthly
  const [preset, setPreset] = useState(50);
  const [custom, setCustom] = useState("");

  const amount = useMemo(() => {
    if (custom !== "") {
      const n = Number(custom);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }
    return preset || 0;
  }, [custom, preset]);

  const monthly = frequency === "monthly";

  const donate = () => {
    if (amount <= 0) {
      toast.error("Please choose an amount", { description: "Select a preset or enter a custom gift amount." });
      return;
    }
    toast.success("Thank you for your support!", {
      description: `Your $${amount}${monthly ? "/mo" : ""} gift \u2014 secure online donations are coming soon. (Demo)`,
    });
  };

  return (
    <section id="donate" aria-labelledby="donate-heading" className="wf-section bg-white">
      <div className="wf-container">
        <div className="grid grid-cols-1 items-stretch gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Left: image + intro */}
          <Reveal className="flex flex-col">
            <span className="inline-flex w-fit items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Make a gift</span>
            <h2 id="donate-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Every gift grows something real</h2>
            <p className="mt-4 max-w-[52ch] text-base leading-7 text-muted-foreground">Choose an amount and see the difference your support makes. One hundred percent of your gift stays rooted in our farmers and their communities.</p>
            <div className="relative mt-6 flex-1 overflow-hidden rounded-sm">
              <img src={SUPPORT_INTRO.sideImage} alt={SUPPORT_INTRO.sideAlt} loading="lazy" className="h-full min-h-[240px] w-full object-cover" />
              <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </Reveal>

          {/* Right: interactive donation card */}
          <Reveal className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-6 shadow-[var(--wf-shadow-sm)] sm:p-8">
            {/* Frequency toggle */}
            <div role="group" aria-label="Giving frequency" className="grid grid-cols-2 gap-1 rounded-full border border-[var(--wf-border)] bg-[var(--wf-paper)] p-1">
              {[{ id: "once", label: "One-time" }, { id: "monthly", label: "Monthly" }].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFrequency(f.id)}
                  aria-pressed={frequency === f.id}
                  data-testid={`donate-frequency-${f.id}`}
                  className={cn(
                    "rounded-full py-2.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    frequency === f.id ? "bg-[var(--wf-maroon)] text-[var(--wf-paper)]" : "text-[var(--wf-soil)] hover:text-[var(--wf-maroon)]"
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Preset amounts */}
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {DONATION_PRESETS.map((p) => {
                const active = custom === "" && preset === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { setPreset(p); setCustom(""); }}
                    aria-pressed={active}
                    data-testid={`donate-preset-${p}`}
                    className={cn(
                      "rounded-sm border py-3 font-serif text-lg font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active
                        ? "border-[var(--wf-maroon)] bg-[var(--wf-maroon)] text-[var(--wf-paper)]"
                        : "border-[var(--wf-border)] bg-[var(--wf-paper)] text-[var(--wf-ink)] hover:border-[var(--wf-maroon)]"
                    )}
                  >
                    ${p}
                  </button>
                );
              })}
            </div>

            {/* Custom amount */}
            <div className="mt-3">
              <label htmlFor="custom-amount" className="sr-only">Custom amount</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-serif text-lg font-semibold text-muted-foreground">$</span>
                <input
                  id="custom-amount"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Other amount"
                  data-testid="donate-custom-input"
                  className="min-h-12 w-full rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] py-3 pl-9 pr-4 font-serif text-lg text-[var(--wf-ink)] placeholder:font-sans placeholder:text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={donate}
              data-testid="donate-submit-button"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--wf-maroon)] px-6 py-4 text-base font-semibold text-[var(--wf-paper)] shadow-[0_10px_28px_rgba(107,31,43,0.35)] transition-colors duration-200 hover:bg-[var(--wf-maroon-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              <Heart className="h-5 w-5" aria-hidden="true" />
              {monthly ? `Give $${amount || 0}/month` : `Donate $${amount || 0}`}
            </button>
            <p className="mt-3 text-center text-xs text-muted-foreground">Demo only — secure online payments are not connected yet.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
